import type * as pub from '#core'

import type { ExportDocument } from './export/exporter-document.ts'
import type { NodeView } from './node-view.ts'
import type { PlaybackDocument } from './playback/playback-document.ts'

export type ViewType<
  TMap extends Partial<Record<pub.AnyNode['type'], NodeView<any, any>>>,
  T extends pub.AnyNode | undefined,
> = T extends undefined
  ? undefined
  : NonNullable<T>['type'] extends keyof TMap
    ? TMap[NonNullable<T>['type']]
    : undefined

/**
 * While the video editor {@link pub.Document} is a [headless](https://en.wikipedia.org/wiki/Headless_software)
 * interface, The `DocumentView` provides methods for using a document for different objectives such as
 * {@link PlaybackDocument playback} and {@link ExportDocument export}. It allows you to create a layer on top
 * of the document tree by creating a custom view of every node and to listen to events on nodes, assets,
 * etc.
 */
export abstract class DocumentView<TMap extends Partial<Record<pub.AnyNode['type'], NodeView<any, any>>>> {
  readonly doc: pub.Document
  protected readonly _map = new Map<string, NonNullable<ViewType<TMap, pub.AnyNode>>>()
  protected readonly _abort = new AbortController()
  isDisposed = false

  constructor(doc: pub.Document) {
    this.doc = doc
  }

  protected _init(): void {
    const { doc } = this
    const listenerOptions = { signal: this._abort.signal }

    doc.on('doc:dispose', this.dispose.bind(this), listenerOptions)

    doc.on('node:create', this.#onCreate.bind(this), listenerOptions)
    doc.on('node:move', this.#onMove.bind(this), listenerOptions)
    doc.on('node:update', this.#onUpdate.bind(this), listenerOptions)
    doc.on('node:delete', this.#onDelete.bind(this), listenerOptions)

    const createViewsFrom = (
      parent: ViewType<TMap, pub.AnyParentNode> | undefined,
      original: pub.AnyNode,
    ) => {
      const view = this.#createAndAddView(original)

      if (parent) view?._move(parent, original.index)
      if ('children' in original)
        original.children.forEach((child) =>
          createViewsFrom(view as ViewType<TMap, pub.AnyParentNode> | undefined, child),
        )
    }

    createViewsFrom(undefined, doc.timeline)
  }

  protected abstract _createView<T extends pub.AnyNode>(original: T): ViewType<TMap, T> | undefined

  #createAndAddView<T extends pub.AnyNode>(original: T): ViewType<TMap, T> | undefined {
    const view = this._createView(original)
    if (view) this._map.set(original.id, view)
    return view
  }

  _getNode<T extends undefined>(original: T): T
  _getNode<T extends pub.AnyNode>(original: T): ViewType<TMap, T>
  _getNode<T extends pub.AnyNode | undefined>(original: T): ViewType<TMap, T>
  _getNode<T extends pub.AnyNode | undefined>(original: T): ViewType<TMap, T> | undefined {
    return original && (this._map.get(original.id) as ViewType<TMap, T>)
  }

  #onCreate({ node }: pub.NodeCreateEvent): void {
    this.#createAndAddView(node)
  }
  #onMove({ node }: pub.NodeMoveEvent): void {
    const { parent } = node

    this._getNode(node)?._move(parent && this._getNode(parent), node.index)
  }
  #onUpdate({ node, key, from }: pub.NodeUpdateEvent): void {
    const view = this._getNode(node)
    if (!view) return
    view._update(key, from)
  }
  #onDelete({ node }: pub.NodeDeleteEvent): void {
    const view = this._getNode(node)
    if (!view) return

    view.dispose()
    this._map.delete(node.id)
  }

  dispose(): void {
    if (this.isDisposed) return
    this.isDisposed = true

    const viewMap = this._map
    viewMap.forEach((view) => view.dispose())
    viewMap.clear()
    this._abort.abort()
  }

  [Symbol.dispose](): void {
    this.dispose()
  }
}
